import { useMemo } from "react";
import axios from "axios";
import useFetch from "../../hooks/useFetch";
import { buildScorecardTable } from "./buildScorecardTable";

/**
 * Data for the Water Quality Scorecard. Four requests, all cached by react-query
 * on their endpoint string, so switching period refetches only the statistics
 * and switching flow or statistic refetches nothing at all — those two are
 * applied client side by buildScorecardTable.
 *
 * The page can show more than one scorecard in a single table: the config and
 * stats endpoints take a comma-separated list of keys, and the builder lays
 * the rows out reach by reach regardless of which card they came from.
 */

export const DEFAULT_SCORECARD_KEY = "watershed";
export const RESERVOIR_SCORECARD_KEY = "reservoir";

/**
 * The period built over the site-wide analysis window. A row in
 * scorecard_periods like the others; see wq_scorecard_live_period.sql.
 */
export const LIVE_PERIOD_KEY = "live";

/**
 * Rebuilds the Live period from the analysis window now in force. The page
 * calls this after the map's own recalculation has finished, so a new window
 * shows up in both places. Resolves to {period_key, rows_written}.
 */
export async function rebuildLivePeriod() {
  const { data } = await axios.post(
    `${process.env.REACT_APP_ENDPOINT}/api/wq-scorecard/rebuild/live`
  );
  return data;
}

const toKeyList = (keys) =>
  (Array.isArray(keys) ? keys : [keys]).filter(Boolean).join(",");

export function useScorecardPeriods() {
  return useFetch("wq-scorecard/periods");
}

export function useScorecardConfig(scorecardKeys = DEFAULT_SCORECARD_KEY) {
  return useFetch(`wq-scorecard/config/${toKeyList(scorecardKeys)}`);
}

export function useScorecardStats(
  period,
  scorecardKeys = DEFAULT_SCORECARD_KEY
) {
  return useFetch(
    `wq-scorecard/stats/${toKeyList(scorecardKeys)}${
      period ? `?period=${period}` : ""
    }`
  );
}

export function useScorecardBenchmarkLines() {
  return useFetch("wq-scorecard/benchmark-lines");
}

/** The five-band color scale, shared with the map's legend and popovers. */
export function useBenchmarkColors() {
  return useFetch("public-map/benchmark-scale-colors");
}

/**
 * Everything the page needs, assembled. Returns the built table plus the
 * loading and error state of the requests behind it.
 *
 * `includeReservoir` appends the reservoir scorecard's rows and columns to the
 * watershed card, so the table reads tributaries first, then the lake.
 */
export default function useWqScorecard({
  scorecardKey = DEFAULT_SCORECARD_KEY,
  includeReservoir = false,
  period,
  flow,
  statistic,
  parameterIds,
}) {
  const scorecardKeys = useMemo(
    () =>
      includeReservoir && scorecardKey !== RESERVOIR_SCORECARD_KEY
        ? [scorecardKey, RESERVOIR_SCORECARD_KEY]
        : [scorecardKey],
    [scorecardKey, includeReservoir]
  );

  const periods = useScorecardPeriods();
  const config = useScorecardConfig(scorecardKeys);
  const stats = useScorecardStats(period, scorecardKeys);
  const colors = useBenchmarkColors();
  const benchmarkLines = useScorecardBenchmarkLines();

  const table = useMemo(
    () =>
      buildScorecardTable({
        config: config.data ?? [],
        stats: stats.data ?? [],
        benchmarkColors: colors.data ?? [],
        flow,
        statistic,
        parameterIds,
        benchmarkLines: benchmarkLines.data ?? [],
      }),
    [
      config.data,
      stats.data,
      colors.data,
      flow,
      statistic,
      parameterIds,
      benchmarkLines.data,
    ]
  );

  const selectedPeriod = useMemo(() => {
    const list = periods.data ?? [];
    return list.find((p) => p.period_key === period) ?? null;
  }, [periods.data, period]);

  // The window currently in force site wide, which the date controls compare
  // against so Apply is only offered when it would actually change something.
  const livePeriod = useMemo(
    () => (periods.data ?? []).find((p) => p.is_live) ?? null,
    [periods.data]
  );

  const benchmarkLineByParameter = useMemo(() => {
    const map = new Map();
    (benchmarkLines.data ?? []).forEach((row) =>
      map.set(row.parameter_index, row)
    );
    return map;
  }, [benchmarkLines.data]);

  const requests = [periods, config, stats, colors, benchmarkLines];

  return {
    table,
    scorecardKeys,
    periods: periods.data ?? [],
    selectedPeriod,
    livePeriod,
    benchmarkColors: colors.data ?? [],
    benchmarkLineByParameter,
    // The table is only worth rendering once config, stats and colors are in;
    // the other two only decorate it.
    isLoading: config.isLoading || stats.isLoading || colors.isLoading,
    isFetching: requests.some((r) => r.isFetching),
    error: requests.find((r) => r.error)?.error ?? null,
    refetchStats: stats.refetch,
    refetchPeriods: periods.refetch,
  };
}
